import React from 'react';
import { LensePanel, type LenseItem } from '../../organisms/LensePanel';
import { Breadcrumb, type BreadcrumbItem } from '../../molecules/Breadcrumb';
import './WorkspacePageLayout.css';

export interface WorkspacePageLayoutProps {
  /** Category items for the selector panel */
  categories: LenseItem[];
  /** Currently selected category ID */
  selectedCategory?: string;
  /** Currently selected subcategory ID */
  selectedSubcategory?: string | null;
  /** Handler for category selection */
  onCategorySelect?: (categoryId: string, subcategoryId: string | null) => void;
  /** Root breadcrumb label (e.g., "Workspace") */
  rootLabel?: string;
  /** Optional class name */
  className?: string;
  /** Content (typically EventFeed) */
  children?: React.ReactNode;
}

/** Default categories for Workspace section */
export const workspaceCategories: LenseItem[] = [
  { id: 'today', name: 'Today' },
  {
    id: 'attention',
    name: 'Attention Required',
    children: [
      { id: 'urgent', name: 'Urgent' },
      { id: 'pending-action', name: 'Pending Action' },
      { id: 'deadlines', name: 'Approaching Deadlines' },
    ],
  },
  {
    id: 'updates',
    name: 'Situation Updates',
    children: [
      { id: 'farm-updates', name: 'Farm Updates' },
      { id: 'regulatory', name: 'Regulatory Changes' },
      { id: 'market', name: 'Market Info' },
    ],
  },
  {
    id: 'obligations',
    name: 'Upcoming Obligations',
    children: [
      { id: 'this-week', name: 'This Week' },
      { id: 'this-month', name: 'This Month' },
      { id: 'this-quarter', name: 'This Quarter' },
    ],
  },
  { id: 'notifications', name: 'Notifications' },
];

/**
 * WorkspacePageLayout - Layout for the Workspace section.
 * Features a selector panel with event categories and an event feed content area.
 */
export const WorkspacePageLayout: React.FC<WorkspacePageLayoutProps> = ({
  categories = workspaceCategories,
  selectedCategory = 'today',
  selectedSubcategory = null,
  onCategorySelect,
  rootLabel = 'Workspace',
  className = '',
  children,
}) => {
  // Build breadcrumb items from selection
  const buildBreadcrumbItems = (): BreadcrumbItem[] => {
    const items: BreadcrumbItem[] = [];
    const category = categories.find(c => c.id === selectedCategory);

    // Root item - always shown, clickable if not on "today"
    items.push({
      label: rootLabel,
      onClick: selectedCategory !== 'today'
        ? () => onCategorySelect?.('today', null)
        : undefined,
    });

    // Category item - only show if not "today" (default)
    if (category && selectedCategory !== 'today') {
      items.push({
        label: category.name,
        onClick: selectedSubcategory ? () => onCategorySelect?.(selectedCategory, null) : undefined,
      });

      // Subcategory item (if selected)
      if (selectedSubcategory && category.children) {
        const subcategory = category.children.find(s => s.id === selectedSubcategory);
        if (subcategory) {
          items.push({
            label: subcategory.name,
          });
        }
      }
    }

    return items;
  };

  return (
    <div className={`workspace-page-layout ${className}`}>
      {/* Selector Panel */}
      <LensePanel
        items={categories}
        selectedItem={selectedCategory}
        selectedSubItem={selectedSubcategory}
        onSelect={onCategorySelect}
        className="workspace-page-layout__selector"
      />

      {/* Main Content Area */}
      <div className="workspace-page-layout__main">
        <header className="workspace-page-layout__header">
          <Breadcrumb items={buildBreadcrumbItems()} />
        </header>
        <div className="workspace-page-layout__content">
          {children}
        </div>
      </div>
    </div>
  );
};

export default WorkspacePageLayout;
